import { lazy, Suspense, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Newspaper, PenSquare, Building2, Briefcase, Calendar, Rocket,
  Users, MapPin, Mail, Handshake, UserCog, Plus, Pencil, Trash2, X, Loader2,
  Settings, Search, BadgeCheck, ArrowLeft, MessageCircle, PanelsTopLeft,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { GoldButton, Pill } from "@/components/Cards";
import BlockEditor from "@/components/BlockEditor";
import { ActivityLog, CmsContentManager, CmsDashboard, HomepageManager, MediaLibrary, NavigationManager, SingletonManager, TaxonomyManager } from "@/components/CmsPanels";
import HeritageAdmin from "@/components/HeritageAdmin";
import EngagementAdmin from "@/components/EngagementAdmin";
import LeadershipManager from "@/components/LeadershipManager";
const SiteBuilder = lazy(() => import("@/components/SiteBuilder"));

const FIELDS = {
  news: [["headline", "text"], ["category", "text"], ["author", "text"], ["excerpt", "textarea"], ["image", "text"], ["read_time", "number"], ["featured", "bool"], ["status", "select:draft,published,archived"]],
  blogs: [["title", "text"], ["category", "text"], ["author", "text"], ["excerpt", "textarea"], ["image", "text"], ["read_time", "number"], ["status", "select:draft,published,archived"]],
  organizations: [["name", "text"], ["type", "text"], ["category", "text"], ["country", "text"], ["city", "text"], ["description", "textarea"], ["website", "text"], ["email", "text"], ["cover", "text"], ["verified", "bool"]],
  opportunities: [["title", "text"], ["category", "text"], ["organization", "text"], ["country", "text"], ["location_type", "text"], ["deadline", "text"], ["excerpt", "textarea"], ["content", "textarea"], ["image", "text"]],
  events: [["name", "text"], ["category", "text"], ["date", "text"], ["time", "text"], ["venue", "text"], ["country", "text"], ["location_type", "text"], ["organizer", "text"], ["excerpt", "textarea"], ["image", "text"]],
  initiatives: [["title", "text"], ["category", "text"], ["status", "text"], ["location", "text"], ["excerpt", "textarea"], ["content", "textarea"], ["image", "text"], ["progress", "number"]],
  partners: [["name", "text"], ["short_name", "text"], ["image_url", "text"], ["url", "text"], ["description", "textarea"], ["status", "select:draft,published,archived"], ["position", "number"]],
  leadership: [["name", "text"], ["position", "text"], ["bio", "textarea"], ["image", "text"], ["linkedin", "text"], ["order", "number"]],
  locations: [["name", "text"], ["country", "text"], ["region", "text"], ["lat", "number"], ["lng", "number"], ["population", "text"], ["role", "text"], ["community", "text"], ["status", "text"], ["description", "textarea"], ["importance", "number"], ["featured", "bool"]],
};
const LABEL = { news: "News", blogs: "Blogs", organizations: "Organizations", opportunities: "Opportunities", events: "Events", initiatives: "Initiatives", partners: "Partners", leadership: "Leadership", locations: "Global Map" };
const TITLE_KEY = { news: "headline", blogs: "title", organizations: "name", opportunities: "title", events: "name", initiatives: "title", partners: "name", leadership: "name", locations: "name" };

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { header: "Content" },
  { key: "cms-articles", label: "Articles", icon: PenSquare },
  { key: "cms-pages", label: "Pages", icon: Newspaper },
  { key: "categories", label: "Categories", icon: Settings },
  { key: "authors", label: "Authors", icon: Users },
  { key: "engagement", label: "Engagement", icon: MessageCircle },
  { key: "homepage", label: "Homepage", icon: LayoutDashboard },
  { key: "news", label: "News", icon: Newspaper },
  { key: "blogs", label: "Blogs", icon: PenSquare },
  { key: "organizations", label: "Organizations", icon: Building2 },
  { key: "opportunities", label: "Opportunities", icon: Briefcase },
  { key: "events", label: "Events", icon: Calendar },
  { key: "initiatives", label: "Initiatives", icon: Rocket },
  { key: "leadership", label: "Leadership", icon: Users },
  { key: "partners", label: "Partners", icon: Handshake },
  { key: "locations", label: "Global Map", icon: MapPin },
  { header: "Heritage" },
  { key: "heritage-communities", label: "Communities & Genealogy", icon: Users },
  { key: "heritage-places", label: "Historical Places", icon: MapPin },
  { key: "heritage-sources", label: "Research Sources", icon: Search },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "messages", label: "Messages", icon: Mail },
  { header: "Media" },
  { key: "media-library", label: "Media Library", icon: BadgeCheck },
  { key: "videos", label: "Videos", icon: BadgeCheck },
  { header: "Website" },
  { key: "site-builder", label: "Site Builder", icon: PanelsTopLeft },
  { key: "navigation", label: "Navigation", icon: Settings },
  { key: "footer", label: "Footer", icon: Settings },
  { key: "branding", label: "Branding", icon: Settings },
  { key: "seo", label: "SEO", icon: Search },
  { header: "Users" },
  { key: "members", label: "Administrators & Editors", icon: UserCog },
  { header: "System" },
  { key: "activity", label: "Activity Log", icon: BadgeCheck },
  { key: "settings", label: "Site Settings", icon: Settings },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="min-h-screen bg-dir-bg" data-testid="admin-page">
      <div className="flex">
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 overflow-y-auto border-r border-dir-gold/15 bg-dir-deep p-4 lg:block">
          <Link to="/" className="mb-6 flex items-center gap-2 px-2 pt-2">
            <img src="/assets/dirnation-logo.png" alt="DIR Nation Admin Studio" className="h-auto w-36 object-contain" />
          </Link>
          <div className="space-y-0.5">
            {NAV.map((n, index) => n.header ? <div key={`${n.header}-${index}`} className="mb-1 mt-5 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-dir-faint">{n.header}</div> : (
              <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => setTab(n.key)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${tab === n.key ? "bg-dir-gold/10 text-dir-gold" : "text-dir-muted hover:bg-dir-card hover:text-dir-text"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </button>
            ))}
          </div>
          <Link to="/" className="mt-4 flex items-center gap-2 px-3 py-2.5 text-sm text-dir-faint hover:text-dir-text"><ArrowLeft className="h-4 w-4" />Back to site</Link>
        </aside>

        <main className="min-w-0 flex-1 lg:ml-60">
          <div className="border-b border-dir-gold/15 bg-dir-deep/60 px-6 py-4 backdrop-blur">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><img src="/assets/dirnation-logo.png" alt="DIR Nation Admin Studio" className="h-10 w-auto object-contain lg:hidden" /><h1 className="font-display text-lg font-bold text-dir-text">Admin · {LABEL[tab] || NAV.find((n) => n.key === tab)?.label}</h1></div>
              <span className="text-sm text-dir-muted">{user?.first_name} · {user?.role}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5 lg:hidden">
              {NAV.filter((n) => n.key).map((n) => <button key={n.key} onClick={() => setTab(n.key)} className={`rounded-full px-2.5 py-1 text-[11px] ${tab === n.key ? "bg-dir-gold text-dir-deep" : "border border-dir-gold/25 text-dir-muted"}`}>{n.label}</button>)}
            </div>
          </div>
          <div className="p-6">
            {tab === "dashboard" && <CmsDashboard />}
            {tab === "cms-articles" && <CmsContentManager resource="articles" label="Articles" />}
            {tab === "cms-pages" && <CmsContentManager resource="pages" label="Pages" />}
            {tab === "categories" && <TaxonomyManager resource="categories" label="Categories" />}
            {tab === "authors" && <TaxonomyManager resource="authors" label="Authors" />}
            {tab === "engagement" && <EngagementAdmin />}
            {tab === "news" && <CmsContentManager resource="news" label="News" />}
            {tab === "blogs" && <CmsContentManager resource="blogs" label="Blogs" />}
            {tab === "organizations" && <CmsContentManager resource="organizations" label="Organizations" />}
            {tab === "opportunities" && <CmsContentManager resource="opportunities" label="Opportunities" />}
            {tab === "events" && <CmsContentManager resource="events" label="Events" />}
            {tab === "initiatives" && <CmsContentManager resource="initiatives" label="Initiatives" />}
            {tab === "leadership" && <LeadershipManager />}
            {tab === "homepage" && <HomepageManager />}
            {tab === "heritage-communities" && <HeritageAdmin resource="communities" />}
            {tab === "heritage-places" && <HeritageAdmin resource="places" />}
            {tab === "heritage-sources" && <HeritageAdmin resource="sources" />}
            {tab === "media-library" && <MediaLibrary />}
            {tab === "videos" && <CmsContentManager resource="videos" label="Videos" />}
            {tab === "navigation" && <NavigationManager />}
            {tab === "site-builder" && <Suspense fallback={<Loader2 className="h-6 w-6 animate-spin text-dir-gold" />}><SiteBuilder /></Suspense>}
            {tab === "footer" && <SingletonManager resource="footer" label="Footer" fields={[{ key: "description", label: "Footer description", type: "textarea" }, { key: "copyright", label: "Copyright" }, { key: "tagline", label: "Tagline" }, { key: "newsletter_title", label: "Newsletter CTA title" }]} />}
            {tab === "branding" && <SingletonManager resource="branding" label="Branding" fields={[{ key: "logo_url", label: "Primary logo URL" }, { key: "alternate_logo_url", label: "Alternate/light logo URL" }, { key: "favicon_url", label: "Favicon URL" }, { key: "site_name", label: "Website name" }, { key: "tagline", label: "Tagline" }]} />}
            {tab === "seo" && <SingletonManager resource="seo" label="SEO" fields={[{ key: "seo_title", label: "Default SEO title" }, { key: "seo_description", label: "Default meta description", type: "textarea" }, { key: "canonical_url", label: "Canonical website URL" }, { key: "og_image", label: "Default Open Graph image" }, { key: "index", label: "Allow search indexing", type: "bool" }]} />}
            {tab === "activity" && <ActivityLog />}
            {FIELDS[tab] && !["news", "blogs", "organizations", "opportunities", "events", "initiatives", "leadership"].includes(tab) && <ResourceManager coll={tab} />}
            {tab === "members" && <Members currentUser={user} />}
            {tab === "newsletter" && <NewsletterList />}
            {tab === "messages" && <Messages />}
            {tab === "settings" && <SiteSettings />}
          </div>
        </main>
      </div>
    </div>
  );
}

function Dash({ setTab }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  const c = data.counts;
  const cards = [["Members", c.members, "members"], ["News", c.news, "news"], ["Blogs", c.blogs, "blogs"], ["Organizations", c.organizations, "organizations"], ["Opportunities", c.opportunities, "opportunities"], ["Events", c.events, "events"], ["Subscribers", c.subscribers, "newsletter"], ["Messages", c.messages, "messages"]];
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([label, val, t]) => (
          <button key={label} onClick={() => setTab(t)} data-testid={`admin-stat-${t}`} className="rounded-2xl border border-dir-gold/15 bg-dir-card p-6 text-left hover:border-dir-gold/50 transition-all">
            <div className="font-display text-3xl font-bold text-gradient-gold">{val}</div>
            <div className="mt-1 text-sm text-dir-muted">{label}</div>
          </button>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-dir-gold/15 bg-dir-card p-6">
          <h3 className="mb-4 font-display font-semibold text-dir-text">Recent Members</h3>
          {data.recent_members.map((m) => <div key={m.id} className="flex justify-between border-b border-dir-gold/10 py-2 text-sm"><span className="text-dir-text">{m.first_name} {m.last_name}</span><span className="text-dir-faint">{m.role}</span></div>)}
        </div>
        <div className="rounded-2xl border border-dir-gold/15 bg-dir-card p-6">
          <h3 className="mb-4 font-display font-semibold text-dir-text">Recent Messages</h3>
          {data.recent_messages.length ? data.recent_messages.map((m) => <div key={m.id} className="border-b border-dir-gold/10 py-2 text-sm"><span className="text-dir-text">{m.name || m.email}</span><span className="ml-2 text-dir-faint">{m.category}</span></div>) : <p className="text-sm text-dir-muted">No messages yet.</p>}
        </div>
      </div>
    </div>
  );
}

function ResourceManager({ coll }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { setItems(null); api.get(`/admin/${coll}`).then(({ data }) => setItems(data)).catch(() => setItems([])); }, [coll]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/${coll}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`admin-resource-${coll}`}>
      <div className="mb-6 flex justify-between">
        <p className="text-sm text-dir-muted">{items ? `${items.length} items` : "Loading..."}</p>
        <GoldButton onClick={() => setEditing({})} data-testid="admin-add-btn"><Plus className="h-4 w-4" />Add New</GoldButton>
      </div>
      {!items ? <Loader2 className="h-6 w-6 animate-spin text-dir-gold" /> : (
        <div className="overflow-hidden rounded-2xl border border-dir-gold/15">
          <table className="w-full text-sm">
            <thead className="bg-dir-deep text-left text-dir-faint">
              <tr><th className="p-4">Title</th><th className="p-4 hidden sm:table-cell">Category</th><th className="p-4 hidden md:table-cell">Status</th><th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-dir-gold/10 hover:bg-dir-card" data-testid={`admin-row-${it.id}`}>
                  <td className="p-4 text-dir-text">{it[TITLE_KEY[coll]]}</td>
                  <td className="p-4 hidden sm:table-cell text-dir-muted">{it.category || it.type || it.region || "—"}</td>
                  <td className="p-4 hidden md:table-cell">{it.status ? <Pill>{it.status}</Pill> : (it.verified ? <span className="text-dir-gold">Verified</span> : "—")}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditing(it)} data-testid={`admin-edit-${it.id}`} className="rounded-lg border border-dir-gold/25 p-2 text-dir-muted hover:text-dir-gold"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`} className="rounded-lg border border-red-500/25 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <ResourceForm coll={coll} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ResourceForm({ coll, item, onClose, onSaved }) {
  const endpoint = coll === "locations" ? "/admin/locations" : `/admin/${coll}`;
  const [f, setF] = useState({ ...item });
  const [loading, setLoading] = useState(false);
  const isNew = !item.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (isNew) await api.post(endpoint, f);
      else await api.put(`${endpoint}/${item.id}`, f);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-10" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl glass p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-form-modal">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-dir-text">{isNew ? "Add" : "Edit"} {LABEL[coll]}</h3>
          <button onClick={onClose} className="text-dir-muted hover:text-dir-gold"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          {FIELDS[coll].map(([name, type]) => {
            const full = type === "textarea";
            return (
              <div key={name} className={full ? "sm:col-span-2" : ""}>
                <label className="mb-1.5 block text-xs uppercase tracking-wider text-dir-faint">{name.replace(/_/g, " ")}</label>
                {type === "textarea" ? (
                  <textarea data-testid={`field-${name}`} rows={3} value={f[name] || ""} onChange={(e) => set(name, e.target.value)} className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-2.5 text-sm text-dir-text focus:outline-none focus:border-dir-gold/60" />
                ) : type === "bool" ? (
                  <label className="flex items-center gap-2 py-2 text-sm text-dir-muted"><input data-testid={`field-${name}`} type="checkbox" checked={!!f[name]} onChange={(e) => set(name, e.target.checked)} className="accent-dir-gold" />Enabled</label>
                ) : type.startsWith("select:") ? (
                  <select data-testid={`field-${name}`} value={f[name] || ""} onChange={(e) => set(name, e.target.value)} className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-2.5 text-sm text-dir-text focus:outline-none">
                    {type.split(":")[1].split(",").map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`field-${name}`} type={type === "number" ? "number" : "text"} value={f[name] ?? ""} onChange={(e) => set(name, type === "number" ? Number(e.target.value) : e.target.value)} className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-2.5 text-sm text-dir-text focus:outline-none focus:border-dir-gold/60" />
                )}
              </div>
            );
          })}
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-dir-gold/25 px-5 py-2.5 text-sm text-dir-muted">Cancel</button>
            <GoldButton data-testid="admin-form-save" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</GoldButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function Members({ currentUser }) {
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [invite, setInvite] = useState({ email: "", first_name: "", last_name: "", role: "member" });
  const load = useCallback(() => { api.get("/auth/admin/users").then(({ data }) => setMembers(data.filter((m) => !q || `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase().includes(q.toLowerCase())))).catch(() => setMembers([])); }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const update = async (id, patch) => {
    try { await api.put(`/auth/admin/users/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const isSuper = currentUser?.role === "super_admin";

  return (
    <div data-testid="admin-members">
      {isSuper && <form onSubmit={async (e) => { e.preventDefault(); try { await api.post("/auth/admin/users", invite); toast.success("User invited"); setInvite({ email: "", first_name: "", last_name: "", role: "member" }); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }} className="mb-6 grid gap-3 rounded-2xl border border-dir-gold/15 bg-dir-card p-5 md:grid-cols-5"><input className="admin-inp" placeholder="First name" value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} /><input className="admin-inp" placeholder="Last name" value={invite.last_name} onChange={(e) => setInvite({ ...invite, last_name: e.target.value })} /><input className="admin-inp md:col-span-2" type="email" required placeholder="Email address" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /><select className="admin-inp" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>{["member", "author", "editor", "admin"].map((r) => <option key={r}>{r}</option>)}</select><button className="rounded-lg bg-dir-gold px-4 py-2 text-sm font-bold text-dir-deep md:col-span-5">Invite user</button></form>}
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-dir-faint" />
        <input data-testid="member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members..." className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 py-2.5 pl-11 pr-4 text-sm text-dir-text focus:outline-none focus:border-dir-gold/60" />
      </div>
      {!members ? <Loader2 className="h-6 w-6 animate-spin text-dir-gold" /> : (
        <div className="overflow-x-auto rounded-2xl border border-dir-gold/15">
          <table className="w-full text-sm">
            <thead className="bg-dir-deep text-left text-dir-faint"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Can publish</th><th className="p-4">Status</th></tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-dir-gold/10" data-testid={`member-row-${m.id}`}>
                  <td className="p-4 text-dir-text">{m.first_name} {m.last_name} {m.email_verified && <BadgeCheck className="inline h-3.5 w-3.5 text-dir-gold" />}</td>
                  <td className="p-4 text-dir-muted">{m.email}</td>
                  <td className="p-4">
                    <select disabled={!isSuper} data-testid={`member-role-${m.id}`} value={m.role} onChange={(e) => update(m.id, { role: e.target.value })} className="rounded-lg bg-dir-elevated border border-dir-gold/20 px-2 py-1 text-xs text-dir-text disabled:opacity-50">
                      {["member", "moderator", "author", "editor", "admin", "super_admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="p-4"><input type="checkbox" disabled={!isSuper || !["editor", "author"].includes(m.role)} checked={!!m.can_publish} onChange={(e) => update(m.id, { can_publish: e.target.checked })} className="accent-dir-gold disabled:opacity-40" /></td>
                  <td className="p-4">
                    <button data-testid={`member-status-${m.id}`} onClick={() => update(m.id, { status: m.status === "active" ? "suspended" : "active" })} className={`rounded-full px-3 py-1 text-xs ${m.status === "active" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>{m.status}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SimpleList({ endpoint, cols, testId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(endpoint).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [endpoint]);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  if (!rows.length) return <p className="text-dir-muted">No records yet.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-dir-gold/15" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-dir-deep text-left text-dir-faint"><tr>{cols.map((c) => <th key={c} className="p-4 capitalize">{c.replace("_", " ")}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-dir-gold/10">{cols.map((c) => <td key={c} className="p-4 text-dir-text">{r[c] || "—"}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function NewsletterList() {
  const [rows,setRows]=useState(null),[country,setCountry]=useState("All Countries");
  useEffect(()=>{api.get("/admin/newsletter").then(({data})=>setRows(data)).catch(()=>setRows([]))},[]);
  if(!rows)return <Loader2 className="h-6 w-6 animate-spin text-dir-gold"/>;
  const countries=["All Countries",...new Set(rows.map(r=>r.country||"Unknown"))],shown=country==="All Countries"?rows:rows.filter(r=>(r.country||"Unknown")===country);
  return <div data-testid="newsletter-list">
    <div className="mb-5 flex items-center justify-between gap-3"><p className="text-sm text-dir-muted">{shown.length} subscribers</p><select aria-label="Country filter" value={country} onChange={e=>setCountry(e.target.value)} className="rounded-xl border border-dir-gold/20 bg-dir-elevated px-4 py-2.5 text-sm text-dir-text outline-none focus:border-dir-gold">{countries.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
    {!shown.length?<p className="text-dir-muted">No subscribers found.</p>:<><div className="space-y-3 md:hidden">{shown.map(r=><article key={r.id||r.email} className="min-w-0 rounded-2xl border border-dir-gold/15 bg-dir-card p-4"><div className="break-all text-sm font-semibold text-dir-text">{r.email}</div><div className="mt-1 text-xs text-dir-muted">{r.first_name||"—"}</div><div className="mt-3 text-sm text-dir-text">{r.country||"Unknown"}</div><div className="text-xs text-dir-muted">{[r.city,r.region].filter(Boolean).join(" / ")||"—"}</div><div className="mt-2 text-[11px] text-dir-faint">{r.created_at?new Date(r.created_at).toLocaleString():"—"}</div></article>)}</div><div className="hidden overflow-x-auto rounded-2xl border border-dir-gold/15 md:block"><table className="min-w-[760px] w-full text-sm"><thead className="bg-dir-deep text-left text-dir-faint"><tr><th className="p-4">Email</th><th className="p-4">First Name</th><th className="p-4">Country</th><th className="p-4">City / Region</th><th className="p-4">Subscribed At</th></tr></thead><tbody>{shown.map(r=><tr key={r.id||r.email} className="border-t border-dir-gold/10"><td className="p-4 text-dir-text">{r.email}</td><td className="p-4 text-dir-text">{r.first_name||"—"}</td><td className="p-4 text-dir-text">{r.country||"Unknown"}</td><td className="p-4 text-dir-muted">{[r.city,r.region].filter(Boolean).join(" / ")||"—"}</td><td className="p-4 text-dir-muted">{r.created_at?new Date(r.created_at).toLocaleString():"—"}</td></tr>)}</tbody></table></div></>}
  </div>
}

function Messages() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/contact-messages").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  if (!rows.length) return <p className="text-dir-muted">No messages yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-messages">
      {rows.map((m) => (
        <div key={m.id} className="rounded-2xl border border-dir-gold/15 bg-dir-card p-6">
          <div className="flex items-center justify-between"><span className="font-semibold text-dir-text">{m.name || m.email}</span><Pill>{m.category}</Pill></div>
          <div className="mt-1 text-xs text-dir-faint">{m.email} {m.organization && `· ${m.organization}`}</div>
          {m.subject && <div className="mt-2 text-sm text-dir-gold">{m.subject}</div>}
          <p className="mt-2 text-sm text-dir-muted">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function SiteSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/site-settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const setStat = (arr, i, key, v) => { const copy = [...s[arr]]; copy[i] = { ...copy[i], [key]: v }; set(arr, copy); };
  const save = async () => {
    setLoading(true);
    try { await api.put("/admin/site-settings", s); toast.success("Settings saved"); } catch { toast.error("Save failed"); }
    setLoading(false);
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-settings">
      <Row label="Hero Badge"><input data-testid="setting-badge" value={s.hero_badge || ""} onChange={(e) => set("hero_badge", e.target.value)} className="admin-inp" /></Row>
      <Row label="Hero Title"><textarea data-testid="setting-title" rows={2} value={s.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className="admin-inp" /></Row>
      <Row label="Hero Subtitle"><textarea data-testid="setting-subtitle" rows={3} value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="admin-inp" /></Row>
      <div>
        <div className="mb-2 text-sm text-dir-muted">Hero Statistics</div>
        {(s.hero_stats || []).map((st, i) => (
          <div key={i} className="mb-2 flex gap-2">
            <input value={st.value} onChange={(e) => setStat("hero_stats", i, "value", e.target.value)} className="admin-inp" placeholder="Value" />
            <input value={st.label} onChange={(e) => setStat("hero_stats", i, "label", e.target.value)} className="admin-inp" placeholder="Label" />
          </div>
        ))}
      </div>
      <GoldButton onClick={save} data-testid="settings-save" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}</GoldButton>
      <style>{`.admin-inp{width:100%;border-radius:12px;background:#0f2822;border:1px solid rgba(212,175,55,0.2);padding:10px 16px;font-size:14px;color:#F7F8F5;outline:none}`}</style>
    </div>
  );
}
function Row({ label, children }) { return <div><label className="mb-1.5 block text-sm text-dir-muted">{label}</label>{children}</div>; }
